<template>
  <v-alert
    v-if="errorMsg"
    class="error-msg"
    :text="errorMsg"
    type="error"
    variant="outlined"
    :closable="true"></v-alert>
  <div class="d-flex flex-column align-center mt-8 intake-detail">
    <v-container :fluid="true" class="lh--container-wide">
      <v-alert
        v-if="!showLoader && insuranceCards !== undefined && insuranceCards.length === 0"
        text="This patient has no insurance cards available"
        type="error"
        variant="outlined"></v-alert>
      <div class="intake-detail__header">
        <h1 class="intake-detail__title">Select Active Insurance for Prior Authorization</h1>
        <span class="intake-detail__stage-chip">
          <span class="intake-detail__stage-dot" />
          Benefit Verification
        </span>
      </div>
      <ReadOnlyBanner v-if="isReadOnlyDevice" />
      <v-container :fluid="true" class="review-container">
        <v-row no-gutters>
          <v-col v-if="!showLoader" cols="12" md="7">
            <PatientBar
              v-if="patientInfo"
              :patient="patientInfo"
              :disabled="true"
              :client-company="intakeOrder?.clientCompany.id" />
          </v-col>
          <v-col v-if="!showLoader" cols="12" md="5">
            <ClassificationBar />
          </v-col>
          <v-container v-if="showLoader">
            <v-skeleton-loader type="text@4" />
          </v-container>
        </v-row>
        <PagePreview
          v-if="currentDocumentPage && !showLoader && requirementsStore.clientConfig"
          ref="previewRef"
          :order-form="intakeOrder.orderForm"
          :insurance-cards="insuranceCards"
          :active-insurance-card-id="activeInsuranceCardId"
          @attribute-edit="onAttributeEdit"
          @select-insurance-card="onSelectInsuranceCard"
          @delete-content="onDeleteContent"
          @snooze="onSnooze"
          @archive="onArchive">
          <template #before>
            <v-col v-if="shouldShowReimbursementTable" cols="12">
              <ReimbursementDataCard :order="intakeOrder" />
            </v-col>
          </template>
        </PagePreview>
        <v-skeleton-loader v-if="showLoader || currentDocumentPage === null" type="image@8" />
        <div v-if="!isReadOnlyDevice" class="document-footer">
          <v-skeleton-loader :loading="showLoader" type="heading">
            <div class="intake-actionbar w-100">
              <v-btn variant="outlined" color="primary" rounded prepend-icon="mdi-arrow-left" @click="goBack">
                Go Back
              </v-btn>
              <div class="intake-actionbar__spacer" />
              <template v-if="insuranceCards.length > 0">
                <v-btn
                  v-if="skipClinicalReview"
                  color="primary"
                  variant="flat"
                  rounded
                  append-icon="mdi-arrow-right"
                  :disabled="!activeInsuranceCardId || buttonDisabled"
                  :loading="showLoader"
                  @click="skipToPriorAuth">
                  Start Prior Authorization
                </v-btn>
                <v-btn
                  v-else-if="!checkPayorInNetwork"
                  color="primary"
                  variant="flat"
                  rounded
                  append-icon="mdi-arrow-right"
                  :disabled="!activeInsuranceCardId"
                  @click="setCheckPayorInNetwork">
                  Check In-Network Status
                </v-btn>
              </template>
            </div>
          </v-skeleton-loader>
        </div>
      </v-container>
      <div v-if="checkPayorInNetwork && !showLoader && !isReadOnlyDevice" class="qualify-form">
        <form class="qualify-card" @submit.prevent="">
          <div class="qualify-card__header">
            <span class="qualify-card__title">Benefit Verification</span>
            <span
              v-if="intakeOrder?.insuranceCard"
              class="qualify-network-chip"
              :class="payorInNetwork ? 'qualify-network-chip--in' : 'qualify-network-chip--out'">
              <span class="qualify-network-chip__dot" />
              {{ payorInNetwork ? "Payor in network" : "Payor not in network" }}
            </span>
          </div>
          <div class="qualify-card__hint">
            An insurance card has been selected. If you wish to proceed please
            {{ returnHomeAfterQualify ? "complete the benefit verification" : "continue to medical necessity" }} or
            select "No" to archive the order.
          </div>
          <div class="qualify-card__controls">
            <v-select
              v-model="continueProcessing"
              :items="['Yes', 'No']"
              label="Do you want to continue processing this patient?" />
            <v-select
              v-if="continueProcessing === 'No'"
              v-model="notifyProvider"
              :items="['Yes', 'No']"
              label="Do you want to notify the referring provider if you will not be processing this patient?" />
          </div>
          <div v-if="notifyProvider === 'Yes' && continueProcessing === 'No'" class="mt-4">
            <label for="qualify-patient-template"> If yes, please update notification template: </label>
            <OutgoingFaxEditor
              v-if="intakeOrder"
              title=""
              :types="[TEMPLATE_TYPES.INTAKE_DISCONTINUE_PROCESSING]"
              :cancel-button="false"
              send-button-text="Send Fax and Archive Order"
              :intake-order="intakeOrder"
              :to-fax-number="intakeOrder.orderForm?.providerFax"
              @on-complete="onNotifyProviderComplete" />
          </div>
        </form>
      </div>
      <div v-if="checkPayorInNetwork && !showLoader && !isReadOnlyDevice">
        <div class="intake-actionbar">
          <div class="intake-actionbar__spacer" />
          <v-btn
            v-if="continueProcessing === 'Yes'"
            color="primary"
            variant="flat"
            rounded
            append-icon="mdi-arrow-right"
            @click="submitStep">
            {{ returnHomeAfterQualify ? "Complete Benefit Verification" : "Continue to Medical Necessity" }}
          </v-btn>
          <v-btn
            v-if="continueProcessing === 'No'"
            color="error"
            variant="tonal"
            rounded
            prepend-icon="mdi-archive-outline"
            @click="onArchive">
            Archive Order
          </v-btn>
        </div>
      </div>
      <IntakeTagBar
        v-if="intakeOrder"
        :tags="intakeOrder.statusTags"
        :tag-defs="availableTags"
        :tag-due-dates="intakeOrder.tagDueDates"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:tags="(t) => (intakeOrder.statusTags = t)"
        @saved="auditLogListRef?.showLatest()"
        @update:tag-due-dates="(d) => (intakeOrder.tagDueDates = d)" />
      <AssigneeBar
        v-if="intakeOrder"
        :assignees="intakeOrder.assignees || []"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:assignees="(a) => (intakeOrder.assignees = a)" />
      <GroupBar
        v-if="intakeOrder"
        :assigned-groups="intakeOrder.assignedGroups || []"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:assigned-groups="(g) => (intakeOrder.assignedGroups = g)" />
      <AuditEntryList
        v-if="intakeOrder"
        ref="auditLogListRef"
        :intake-order-id="intakeOrder.id"
        :tag-defs="availableTags" />
    </v-container>
  </div>
  <v-dialog v-model="archiveReasonDialog" max-width="800">
    <ArchiveNote :intake-order="intakeOrder" @on-archived="onArchived" />
  </v-dialog>
</template>

<script setup>
// Core imports
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useDate, useGoTo } from "vuetify";

// Component imports
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import AuditEntryList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import ArchiveNote from "@/components/ArchiveNote.vue";
import ReimbursementDataCard from "@/components/ReimbursementDataCard.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import { useIntakeActions } from "@/composables/useIntakeActions";
import { useResponsive } from "@/composables/useResponsive";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";

// Service and model imports
import { INTAKE_STAGE, IntakeDocumentPage, IntakeInsuranceCard, IntakeOrder, Patient } from "@/models/intake";
import { intakeService } from "@/services/intakeService";
import router from "@/router";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { handleFormValidationErrors } from "@/utils/validators";

const intakeActions = useIntakeActions();
const requirementsStore = useRequirementsStore();
const intakeStore = useIntakeStore();
const filtersStore = useIntakeListFiltersStore();
const dateAdapter = useDate();
const showLoader = ref(true);
const previewRef = ref(null);
const auditLogListRef = ref(null);
const intakeOrder = computed(() => intakeStore.intakeOrder);
const insuranceCards = computed(() => intakeStore.insuranceCards);
const currentDocumentPage = ref(null);
const activeInsuranceCardId = ref(null);
const pageChanger = intakeStore.pageManager;
const errorMsg = ref(null);
const patientInfo = ref(null);
const payorInNetwork = ref(false);
const continueProcessing = ref("Yes");
const notifyProvider = ref("No");
const checkPayorInNetwork = ref(false);
const buttonDisabled = ref(false);
const skipClinicalReview = computed(() => requirementsStore.clientConfig?.intake?.skipClinicalReview ?? false);
// When set, this client splits intake across teams: completing Qualify returns home instead of
// advancing into Clinical Review (LAM-3943). Defaults to false to preserve the combined flow.
const returnHomeAfterQualify = computed(() => requirementsStore.clientConfig?.intake?.returnHomeAfterQualify ?? false);
const availableTags = computed(() => requirementsStore.clientConfig?.intake?.availableTags || []);
const archiveReasonDialog = ref(false);

const route = useRoute();
const scrollTo = useGoTo();
const { isReadOnlyDevice } = useResponsive();

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.QUALIFY_PATIENT;
  await fetchIntakeOrder();
  await requirementsStore.refreshClientConfig(intakeOrder.value.clientCompany.id);
});

onUnmounted(() => {
  requirementsStore.refreshCurrentUserClientConfig();
});

window.addEventListener("beforeunload", () => {
  // Fire and forget the save updates
  insuranceCards.value?.map((card) => card.saveUpdates());
  intakeOrder.value?.saveUpdates();
});

const markAsActiveInsurance = async (card) => {
  errorMsg.value = "";
  try {
    const order = await intakeService.patchIntakeOrder(intakeOrder.value.id, { insuranceCardId: card.id });
    intakeStore.intakeOrder = new IntakeOrder(order, dateAdapter);
    activeInsuranceCardId.value = intakeStore.intakeOrder.insuranceCard?.id;
    updatePayorInNetwork();
  } catch (e) {
    errorMsg.value = "Failed to mark insurance card as active";
  }
};

const updatePayorInNetwork = () => {
  const card = insuranceCards.value.find((card) => card.id === activeInsuranceCardId.value);
  payorInNetwork.value = (intakeOrder.value.payorNames ?? []).indexOf(card?.payorName) !== -1;
};

const onSelectInsuranceCard = async (cardId) => {
  const card = insuranceCards.value.find((c) => c.id === cardId);
  pageChanger.goToDataFormPage(card);
  checkPayorInNetwork.value = false;
  await markAsActiveInsurance(card);
};

const onAttributeEdit = (field, value, type, id) => {
  if (type === "ORDER_FORM") {
    intakeOrder.value.orderForm.updateAttribute(field.key, value, type, id);
    intakeOrder.value.orderForm.debouncedSaveUpdates();
  } else if (type === "INSURANCE_CARD") {
    const card = insuranceCards.value.find((card) => card.id === id);
    if (card) {
      card.updateAttribute(field.key, value, type);
      card.debouncedSaveUpdates();
      if (field.key === "payorName") {
        updatePayorInNetwork();
      }
    }
  }
};

const onDeleteContent = async (content, documentType) => {
  if (!(await intakeActions.confirmDeleteContent(documentType))) {
    return;
  }
  if (!(await saveAnyPendingData())) {
    return;
  }
  errorWrapper(async () => {
    await intakeActions.onDeleteContentConfirm(content, documentType);
    await fetchIntakeOrder();
  }, "Error deleting the content");
};

const saveAnyPendingData = async () => {
  return await errorWrapper(async () => {
    await Promise.all([...insuranceCards.value.map((card) => card.saveUpdates()), intakeOrder.value.saveUpdates()]);
  }, "Error saving any pending data");
};

const setCheckPayorInNetwork = () => {
  checkPayorInNetwork.value = true;
  scrollTo(".document-footer", { behavior: "smooth", duration: 1000 });
};

const skipToPriorAuth = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  if (previewRef.value.formErrors.length > 0) {
    errorMsg.value = "There are errors in the insurance card. Please make sure that all required fields are filled out";
    showLoader.value = false;
    buttonDisabled.value = false;
    return;
  }
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await intakeOrder.value.saveUpdates();
    await intakeService.postOrdersSubmitClinicalReview(intakeOrder.value.id);
    // Redirect to kanban board, depending on submission method (i.e. CoverMyMeds) some orders will
    // require standby time before status will switch from Created to Requires Review
    router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
  } catch (ex) {
    errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const submitStep = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  const isValid = await handleFormValidationErrors(previewRef.value.formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
    showLoader.value = false;
    return;
  }
  if (!intakeOrder.value.insuranceCard) {
    errorMsg.value = "No active insurance card selected";
    showLoader.value = false;
    return;
  }
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await intakeService.postOrderSubmitQualify(intakeOrder.value.id);
    if (returnHomeAfterQualify.value) {
      // This client's Clinical Review is handled by a separate team (LAM-3943); return home.
      router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
    } else {
      router.push({ name: "document-review-order", params: { id: intakeOrder.value.id } });
    }
  } catch (e) {
    errorMsg.value = "Failed to submit the Draft Order";
  }
  showLoader.value = false;
};

const onNotifyProviderComplete = ({ sent }) => {
  if (sent !== null) {
    onArchive();
  }
};

const fetchPatientQualifyDocuments = async (patientId) => {
  errorMsg.value = "";
  try {
    const [cardResult, pageResult] = await Promise.all([
      intakeService.getPatientInsuranceCards(patientId),
      intakeService.getPatientDocumentPages(patientId),
    ]);
    intakeStore.insuranceCards = cardResult.results.map((r) => new IntakeInsuranceCard(r, dateAdapter));
    const allPages = pageResult.results
      .map((p) => new IntakeDocumentPage(p, dateAdapter))
      .sort((a, b) =>
        a.documentKey === b.documentKey
          ? (a.pageNumber ?? 0) - (b.pageNumber ?? 0)
          : a.documentKey.localeCompare(b.documentKey)
      );
    pageChanger.pages = allPages.length ? allPages : insuranceCards.value.map((card) => card.pages[0]);
    // Land on the active (or first) insurance card's page; all patient pages remain navigable.
    const initialCard =
      insuranceCards.value.find((card) => card.id === activeInsuranceCardId.value) ?? insuranceCards.value[0];
    if (initialCard) {
      pageChanger.goToDataFormPage(initialCard);
    }
    currentDocumentPage.value = pageChanger.currentPageRef;
    return true;
  } catch (e) {
    errorMsg.value = `Failed to fetch patient documents: ${e.message || e}`;
    return false;
  }
};

const fetchIntakeOrder = async () => {
  errorMsg.value = "";
  try {
    const result = await intakeService.getIntakeOrder(route.params.id);
    intakeStore.intakeOrder = new IntakeOrder(result, dateAdapter);
    patientInfo.value = new Patient(result.patient, dateAdapter);
    const patientId = intakeOrder.value.patient.id;
    activeInsuranceCardId.value = intakeOrder.value.insuranceCard?.id;
    const cardsFetched = await fetchPatientQualifyDocuments(patientId);
    if (cardsFetched && !activeInsuranceCardId.value && insuranceCards.value.length === 1 && !isReadOnlyDevice.value) {
      await markAsActiveInsurance(insuranceCards.value[0]);
    }
    updatePayorInNetwork();
  } catch (e) {
    errorMsg.value = `Error fetching the Draft Order: ${e.message || e}`;
  } finally {
    showLoader.value = false;
  }
};

const goBack = () => {
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const errorWrapper = async (fn, errorPrefix) => {
  showLoader.value = true;
  try {
    await fn();
    return true;
  } catch (e) {
    errorMsg.value = `${errorPrefix} ${e.message}`;
  } finally {
    showLoader.value = false;
  }
  return false;
};

const onSnooze = async ({ minutes }) => {
  await errorWrapper(async () => {
    intakeStore.intakeOrder = await intakeActions.onSnooze({ orderId: intakeOrder.value.id, minutes });
  }, "Error snoozing the order");
};

const onArchive = async () => {
  if (intakeOrder.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      showLoader.value = true;
      await intakeService.deleteArchiveOrder(intakeOrder.value.id);
    } catch (e) {
      errorMsg.value = `Error unarchiving the order: ${e.message}`;
    } finally {
      showLoader.value = false;
      archiveReasonDialog.value = false;
    }
  } else {
    archiveReasonDialog.value = true;
  }
};

const onArchived = () => {
  archiveReasonDialog.value = false;
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const shouldShowReimbursementTable = computed(() => {
  // LAM-4074: hidden for all clients; restore the reimbursementTableEnabled config check to re-enable.
  return false;
});
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
